import type { ReactNode } from 'react';

import { useId } from 'react';
import { DateTime } from 'luxon';
import { CalendarDays } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';

import { cn } from '@shared/utils/cn';
import { Button } from '@shared/components/ui/button';
import { Calendar } from '@shared/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';

import { FieldShell } from './field-shell';

type Props = {
  name: string;
  label?: ReactNode;
  helperText?: ReactNode;
};

const toDateTime = (value: unknown): DateTime | null => {
  if (!value) return null;
  if (DateTime.isDateTime(value)) return value;
  if (value instanceof Date) return DateTime.fromJSDate(value);
  if (typeof value === 'string') return DateTime.fromISO(value);
  return null;
};

/** Sélecteur de date (Popover + Calendar), la valeur du formulaire est une date ISO `yyyy-MM-dd`. */
export const RHFDatePicker = ({ name, label, helperText }: Props) => {
  const { control } = useFormContext();
  const id = useId();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const date = toDateTime(field.value);

        return (
          <FieldShell error={error?.message} helperText={helperText} id={id} label={label}>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  aria-invalid={error ? true : undefined}
                  className={cn(
                    'h-14 w-full justify-start px-3.5 text-base font-normal',
                    !date && 'text-muted-foreground'
                  )}
                  id={id}
                  type="button"
                  variant="outline"
                >
                  <CalendarDays />
                  {date?.isValid ? date.setLocale('fr').toLocaleString(DateTime.DATE_FULL) : 'Choisir une date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <Calendar
                  defaultMonth={date?.toJSDate()}
                  mode="single"
                  onSelect={(selected) =>
                    selected && field.onChange(DateTime.fromJSDate(selected).toFormat('yyyy-MM-dd'))
                  }
                  selected={date?.toJSDate()}
                />
              </PopoverContent>
            </Popover>
          </FieldShell>
        );
      }}
    />
  );
};
